/**
 * Customer-run USDA rural eligibility check for one property. Never runs on
 * its own — the buyer clicks to look the address up on USDA's eligibility
 * map, and the result is saved on the property.
 */
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Link, Stack, Typography } from '@mui/material';
import { toast } from 'sonner';

import { useCheckUsdaEligibilityMutation } from '@/apis/extended/productApi';
import type { UsdaEligibility } from '@/types/ProductType';

export const USDA_LOOKUP_URL =
  'https://eligibility.sc.egov.usda.gov/eligibility/welcomeAction.do?pageAction=sfp';

interface UsdaEligibilityCheckProps {
  productId?: string;
  eligibility?: UsdaEligibility;
  compact?: boolean;
}

export const UsdaEligibilityCheck = ({
  productId,
  eligibility,
  compact = false,
}: UsdaEligibilityCheckProps) => {
  const [checkUsda, { isLoading }] = useCheckUsdaEligibilityMutation();
  const [message, setMessage] = useState<string | null>(null);
  const status = eligibility?.status;

  const handleCheck = async () => {
    if (!productId) return;
    setMessage(null);
    try {
      const result = await checkUsda(productId).unwrap();
      if (result.status === 'unknown') {
        setMessage(result.message ?? null);
      }
    } catch {
      toast.error(
        "USDA's eligibility service didn't respond. Try again, or check on the USDA site."
      );
    }
  };

  const summary =
    status === 'eligible'
      ? 'USDA eligible — this address is in a USDA rural area.'
      : status === 'ineligible'
      ? 'Not USDA eligible — this address is outside USDA rural areas.'
      : 'USDA loans are only for eligible rural addresses.';

  return (
    <Stack spacing={0.5} sx={{ mt: compact ? 0.5 : 1 }}>
      <Typography
        variant="caption"
        sx={{
          color:
            status === 'eligible'
              ? 'success.main'
              : status === 'ineligible'
              ? 'error.main'
              : 'warning.main',
          fontWeight: 500,
        }}
      >
        {summary}
        {eligibility?.checkedAt &&
          ` Checked ${new Date(eligibility.checkedAt).toLocaleDateString()}.`}
      </Typography>
      {message && (
        <Typography variant="caption" color="text.secondary">
          {message}
        </Typography>
      )}
      <Stack direction="row" spacing={1.5} alignItems="center">
        {productId && (
          <LoadingButton
            size="small"
            variant="outlined"
            loading={isLoading}
            onClick={handleCheck}
            sx={{ textTransform: 'none', py: 0.25 }}
          >
            {status && status !== 'unknown'
              ? 'Check again'
              : 'Check USDA eligibility'}
          </LoadingButton>
        )}
        <Link
          href={USDA_LOOKUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
        >
          Open USDA site
        </Link>
      </Stack>
    </Stack>
  );
};
